import { useMemo } from 'react';

import {
  type PaymentSubscription,
  TierName,
  TierStatusName,
} from '@order/graphql';
import {
  getPreferredSubscription,
  useFreeTrialPlanId,
  useSweetpassPlan,
} from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  useSweetpassMembershipQuery,
  useSweetpassMembershipWithExpirationStateQuery,
} from '../../GraphQL/SweetpassMembership.generated';
import { useCancelSweetpassMembership } from '../useCancelSweetpassMembership';
import { useReactivateSweetpassMembership } from '../useReactivateSweetpassMembership';

/**
 * The sweetpass membership data is re-fetched on focus to ensure it is in sync after tier operations.
 * The plan data depends on the current tier, so it will also be automatically re-fetched through dependencies.
 * The membership query uses `network-only` so we don't show outdated information.
 */
export const useSweetpassMembershipData = () => {
  const shouldFetchWithExpirationState = useFeatureFlag(
    'CELS-1558-subscription-credit-card-expiration-state',
  );

  const [responseWithExpirationState, fetchMembershipWithExpirationState] =
    useSweetpassMembershipWithExpirationStateQuery({
      requestPolicy: 'network-only',
      pause: true,
    });

  const [legacyResponse, fetchLegacyMembership] = useSweetpassMembershipQuery({
    requestPolicy: 'network-only',
    pause: true,
  });

  const response = shouldFetchWithExpirationState
    ? responseWithExpirationState
    : legacyResponse;

  const fetchMembership = shouldFetchWithExpirationState
    ? fetchMembershipWithExpirationState
    : fetchLegacyMembership;

  const {
    data: sweetpassMembershipData,
    error: loyaltyProfileError,
    fetching: isLoadingMembership,
  } = response;

  // ─── Loyalty Profile Data ────────────────────────────────────────────

  const loyaltyProfile = sweetpassMembershipData?.loyaltyProfile;
  const joinDate = loyaltyProfile?.joinDate;
  const tierStatus = loyaltyProfile?.tierStatus?.name;
  const tier = loyaltyProfile?.tier;
  const nextTier = loyaltyProfile?.tier?.nextTier;
  const hasPaidTierHistory = Boolean(loyaltyProfile?.hasPaidTierHistory);
  const trialEligibility = loyaltyProfile?.trialEligible?.[0]?.duration ?? 0;
  const isEligibleToFreeTrial = trialEligibility > 0;

  // ─── Subscriptions Data ──────────────────────────────────────────────

  const subscriptions: ReadonlyArray<Partial<PaymentSubscription>> =
    sweetpassMembershipData?.getSubscriptions ?? [];
  const activeSubscription = getPreferredSubscription(subscriptions);
  const subscriptionId = activeSubscription?.id;
  const nextBillingDate = activeSubscription?.nextBillingDate;

  // ─── Payment Methods Data ────────────────────────────────────────────

  const paymentMethods =
    sweetpassMembershipData?.paymentSubscriptionProfile?.creditCards;

  // ─── Plan Data ───────────────────────────────────────────────────────

  const { freeTrialPlanId, isLoadingFreeTrialPlanId } = useFreeTrialPlanId();
  const { planPrice, billingFrequencyUnit, isLoadingPlan } = useSweetpassPlan({
    requestedTierId: nextTier?.id,
    pause: !nextTier?.id,
  });

  // ─── Loading State ───────────────────────────────────────────────────

  const isSweetpassMembershipLoading =
    isLoadingMembership || isLoadingPlan || isLoadingFreeTrialPlanId;

  // ─── Error State ─────────────────────────────────────────────────────
  // This only considers errors if the tier is missing or if the plan is required but is missing.

  const isMissingTier = !tier;
  const isPlanRequired = tier?.name === TierName.Sweetpass;
  const isMissingPlan = !planPrice || !billingFrequencyUnit;
  const isMissingRequiredPlan = isPlanRequired && isMissingPlan;
  const hasMembershipError = isMissingTier || isMissingRequiredPlan;

  // ─── Callbacks ───────────────────────────────────────────────────────

  const { cancelSubscriptionAtCycleEnd } = useCancelSweetpassMembership({
    subscriptionId,
    nextBillingDate,
    tierName: tier?.name,
    isFreeTrial: tierStatus === TierStatusName.FreeTrial,
    refetchMembership: fetchMembership,
  });

  const { reactivateSubscription } = useReactivateSweetpassMembership({
    subscriptionId,
    refetchMembership: fetchMembership,
  });

  return useMemo(
    () => ({
      isSweetpassMembershipLoading,
      tierStatus,
      tier,
      nextTier,
      freeTrialPlanId,
      planPrice,
      billingFrequencyUnit,
      hasPaidTierHistory,
      isEligibleToFreeTrial,
      joinDate,
      activeSubscription,
      paymentMethods,
      loyaltyProfileError,
      hasMembershipError,
      cancelSubscriptionAtCycleEnd,
      reactivateSubscription,
      refetchMembership: fetchMembership,
    }),
    [
      isSweetpassMembershipLoading,
      tierStatus,
      tier,
      nextTier,
      freeTrialPlanId,
      planPrice,
      billingFrequencyUnit,
      hasPaidTierHistory,
      isEligibleToFreeTrial,
      joinDate,
      activeSubscription,
      paymentMethods,
      loyaltyProfileError,
      hasMembershipError,
      cancelSubscriptionAtCycleEnd,
      reactivateSubscription,
      fetchMembership,
    ],
  );
};
